import type { ReactNode } from "react"
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from "@tanstack/react-router"
import type { QueryClient } from "@tanstack/react-query"
import { PostHogProvider } from "@posthog/react"
import appCss from "@/styles.css?url"
import { Toaster } from "sonner"
import { posthog } from "@/core/posthog"
import { ThemeProvider, themeInitScript } from "@/lib/theme"
import { PostHogTracker } from "@/lib/posthog"

// Root route. Holds the SSR document shell and the per-request QueryClient in
// router context (consumed by loaders for prefetch + dehydration).
export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, viewport-fit=cover",
      },
      { name: "color-scheme", content: "light dark" },
      { title: "Attend Console" },
    ],
    links: [{ rel: "stylesheet", href: appCss }],
  }),
  component: RootComponent,
})

function RootComponent() {
  return (
    <RootDocument>
      <PostHogProvider client={posthog}>
        <ThemeProvider>
          <PostHogTracker />
          <Outlet />
          <Toaster position="top-center" richColors closeButton />
        </ThemeProvider>
      </PostHogProvider>
    </RootDocument>
  )
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        {/* Set the .dark class before first paint to avoid a theme flash. */}
        <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  )
}
